import { TEST_TYPES } from 'Constants';
import {
  PRESENTATION_DEFAULT_LEFT_MARGIN,
  PRESENTATION_DEFAULT_SIZE,
  createPresentation,
  createTableForVerificationHeatmapSummary,
  createTemplateSlideForPresentation,
  drawImageWithHeatmapDataOnCanvas,
} from 'Utils/presentationUtils';
import moment from 'moment';
import React, { useState } from 'react';
import { generatePath } from 'react-router';
import { useNavigate, useParams } from 'react-router-dom';

import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import {
  Box,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import floorplansApi from 'Api/floorPlans';
import resultsApi from 'Api/results';
import segmentsApi from 'Api/segments';
import testsApi from 'Api/tests';

import { fileNameUtil, getScaledImageSize } from 'Components/Floorplans/utils';
import NoDataDisplay from 'Components/UI/NoDataDisplay';

import { ROUTE_PROJECT_FLOORPLAN_HEATMAP } from 'Config/routes';

import {
  getCalcResultLabelFromVerLikeTest,
  getDataFromResponse,
  getErrorMessageFromResponse,
  isValidResponse,
} from 'Utils';

const HeatmapsListByFloor = ({ projectId, heatmaps }) => {
  const navigate = useNavigate();
  const { buildingId } = useParams();

  const floorsFilesConfig = heatmaps.reduce((acc, heatmap) => {
    const { floorplanid, mainfloorplanid, mainfpname, filename, floornumber } =
      heatmap;
    const intFloorNumber = parseInt(floornumber, 10) || -1;
    const mainFpId = mainfloorplanid || floorplanid;
    const mainFpName = mainfpname || filename;

    if (!acc[intFloorNumber]) {
      acc[intFloorNumber] = {
        ids: [],
        floorplans: [],
      };
    }
    if (acc[intFloorNumber].ids.includes(mainFpId)) {
      return acc;
    }

    acc[intFloorNumber].ids.push(mainFpId);
    acc[intFloorNumber].floorplans.push({
      id: mainFpId,
      name: mainFpName,
    });

    return acc;
  }, {});

  const getFloorNumberHeaderText = (floorNumber) => {
    const intFloorNumber = parseInt(floorNumber, 10);
    if (intFloorNumber === -1) {
      return 'Other';
    }
    return `Floor ${intFloorNumber}`;
  };

  const handleFloorplanClick = (event, floorplanId) => {
    const _path = generatePath(ROUTE_PROJECT_FLOORPLAN_HEATMAP, {
      projectId,
      floorplanId,
      buildingId,
    });
    /** This is for opening it in new tab through command + click or ctrl + click */
    if (event.metaKey || event.ctrlKey) {
      window.open(_path);
      return;
    }
    navigate(_path);
  };

  return (
    <Stack spacing={2}>
      {Object.keys(floorsFilesConfig).map((floorNumber) => {
        return (
          <Stack key={`floor-${getFloorNumberHeaderText(floorNumber)}`}>
            <Typography variant="h6">
              {getFloorNumberHeaderText(floorNumber)}
            </Typography>
            <Stack sx={{ mt: 1, ml: 2 }} spacing={1}>
              {floorsFilesConfig[floorNumber].floorplans.map((floorplan) => {
                return (
                  <Stack
                    key={`project-${projectId}-fp-${floorplan.id}`}
                    direction="row"
                    spacing={1}
                  >
                    <ImageIcon color="disabled" />
                    <Typography
                      key={floorplan.id}
                      gutterBottom
                      variant="body1"
                      component="div"
                      onClick={(e) => handleFloorplanClick(e, floorplan.id)}
                      sx={{
                        '&:hover': {
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {floorplan.name}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};

export default function ResultsBuildingProjectsDisplay({ projects, loading }) {
  const [downloadingHeatmapsProjectId, setDownloadingHeatmapsProjectId] =
    useState(null);
  if (loading) {
    return <ResultsBuildingProjectsDisplaySkeleton />;
  }

  if (!loading && (!projects || !projects.length)) {
    return (
      <Box sx={{ height: '500px', width: '100%', display: 'flex' }}>
        <NoDataDisplay />
      </Box>
    );
  }
  const handleProjectHeatmapsDownload = async (projectId) => {
    try {
      setDownloadingHeatmapsProjectId(projectId);

      const heatmapsResponse = await resultsApi.getHeatMapListForProject(
        projectId,
        null,
        false,
      );
      if (!isValidResponse(heatmapsResponse)) {
        throw new Error(getErrorMessageFromResponse(heatmapsResponse));
      }

      const heatmaps = getDataFromResponse(heatmapsResponse).filter(
        ({ testtype }) => testtype === TEST_TYPES.VERIFICATION,
      );

      const floorplansByHeatmapData = {};
      const uniqueFloorplans = new Set();

      for (let heatmap of heatmaps) {
        const {
          floorplanid,
          filename,
          mainfloorplanid,
          mainfpname,
          heatmapid,
          metadata,
        } = heatmap;
        const floorplanId = mainfloorplanid || floorplanid;
        floorplansByHeatmapData[heatmapid] = {
          id: floorplanId,
          name: mainfpname || filename,
          metadata,
          url: '',
        };
        if (floorplanId) {
          uniqueFloorplans.add(floorplanId);
        }
      }

      const loadFloorplan = async (floorPlanId) => {
        const fpResp = await floorplansApi.getPresignedURL({
          projectId,
          floorPlanId,
          heatmapVersion: 1,
        });
        if (!fpResp.data.preSignedURL) return null;
        const floorplanImg = await new Promise((resolve) => {
          const img = new Image();
          img.src = fpResp.data.preSignedURL;
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
        });
        return {
          url: fpResp.data.preSignedURL,
          img: floorplanImg,
          id: floorPlanId,
        };
      };

      const [
        heatmapsDetailedResponse,
        segmentsData,
        floorplansDataResponse,
        displayNumbersResponse,
        ...loadedFloorplansDataArr
      ] = await Promise.all([
        resultsApi.getHeatMapListDetailed(
          heatmaps.map(({ heatmapid }) => heatmapid),
        ),
        segmentsApi.getSegments(null, projectId),
        floorplansApi.getFloorPlans(projectId, null),
        testsApi.getDisplayNumbersProject(parseInt(projectId, 10)),
        ...Array.from(uniqueFloorplans).map((fpId) => loadFloorplan(fpId)),
      ]);

      if (
        !isValidResponse(heatmapsDetailedResponse) ||
        !isValidResponse(segmentsData) ||
        !isValidResponse(displayNumbersResponse) ||
        !isValidResponse(floorplansDataResponse) ||
        !loadedFloorplansDataArr.reduce((acc, fpData) => acc && !!fpData, true)
      ) {
        throw new Error();
      }

      const floorplansInProjectData = getDataFromResponse(
        floorplansDataResponse,
      ).reduce((acc, { filename, metadata }) => {
        acc[filename] = metadata;
        return acc;
      }, {});

      const projectTestDisplayNumbers = getDataFromResponse(
        displayNumbersResponse,
      );
      const floorplansDataObj = loadedFloorplansDataArr.reduce(
        (acc, { url, id, img }) => {
          acc[id] = {
            url,
            img,
          };
          return acc;
        },
        {},
      );
      const heatmapsDetailed = getDataFromResponse(heatmapsDetailedResponse);

      const pres = createPresentation();

      for (let [floorplanId, floorplanData] of Object.entries(
        floorplansDataObj,
      )) {
        const heatmapsDataByScenarios = {};
        const _heatmaps = heatmaps.filter(
          ({ floorplanid, mainfloorplanid }) =>
            floorplanid === parseInt(floorplanId) ||
            mainfloorplanid === parseInt(floorplanId),
        );

        for (let heatmapData of Object.values(_heatmaps)) {
          const scenarioIndex = parseInt(heatmapData?.scenarioNumber) || 1;
          if (!heatmapsDataByScenarios[scenarioIndex]) {
            heatmapsDataByScenarios[scenarioIndex] = [];
          }
          heatmapsDataByScenarios[scenarioIndex].push(heatmapData);
        }

        for (let [scenarioIndex, heatmapsData] of Object.entries(
          heatmapsDataByScenarios,
        )) {
          const heatmapsIds = heatmapsData.map(({ heatmapid }) => heatmapid);
          const heatmapsInScenario = Object.values(heatmapsDetailed).filter(
            ({ heatmapid }) => !!heatmapsIds.includes(heatmapid),
          );

          floorplanData.floorNumber =
            heatmapsInScenario[0].heatmaps[0].floornumber;
          floorplanData.name = heatmapsInScenario[0].heatmaps[0].floorplan;

          const earliestSurveyDate = heatmapsInScenario.reduce(
            (earliest, current) => {
              const surveyDate = new Date(current.surveydate);
              return surveyDate < earliest ? surveyDate : earliest;
            },
            heatmapsInScenario[0].surveydate,
          );
          let subtitle = `${heatmapsInScenario[0].buildingname}`;
          if (floorplanData.floorNumber) {
            subtitle += ` - Floor ${floorplanData.floorNumber}`;
          }
          let footer = `${moment(earliestSurveyDate).format(
            'DD-MM-YYYY',
          )} PID:${projectId}`;

          const slideTitle = fileNameUtil.removeExtension(floorplanData.name);

          const slide = createTemplateSlideForPresentation(
            pres,
            slideTitle,
            subtitle,
            {
              width: PRESENTATION_DEFAULT_SIZE.w,
            },
            footer,
          );

          // Table
          const testResultsObj = {};
          for (let {
            testid,
            calculatedresult,
            testname,
          } of heatmapsInScenario) {
            const testResult =
              getTestResultDisplayDataFromCalculatedResult(calculatedresult);
            let eACH = '';
            if (typeof testResult.value === 'number') {
              eACH =
                parseInt(
                  Math.round(
                    -100 *
                      Math.log10(1 - testResult.value / 100) *
                      Math.log(10),
                  ),
                ) / 100;
            }

            testResultsObj[projectTestDisplayNumbers[testid]] = {
              result: getCalcResultLabelFromVerLikeTest(
                { calculatedresult },
                true,
              ),
              name: testname,
              hourlyAerosolRemoval: testResult.value,
              eACH,
            };
          }

          const testNumbers = Object.keys(testResultsObj).sort(
            (a, b) => parseInt(a) - parseInt(b),
          );
          const data = [];

          for (let testNum of testNumbers) {
            data.push([
              testResultsObj[testNum].name,
              testResultsObj[testNum].hourlyAerosolRemoval,
              testResultsObj[testNum].eACH,
              testResultsObj[testNum].result,
            ]);
          }

          slide.addTable(...createTableForVerificationHeatmapSummary(data));
          slide.addText(
            [
              {
                text: 'Comments',
                options: {
                  fontSize: 16,
                  color: '000000',
                  align: 'left',
                  breakLine: true,
                  bold: true,
                  fontFace: 'Lato',
                  autoFit: true,
                },
              },
            ],
            {
              x: 0.1,
              y: 4,
              w: PRESENTATION_DEFAULT_LEFT_MARGIN - 0.2,
              h: 2.0,
              valign: 'top',
              autoFit: true,
            },
          );

          // Image

          let imageObj = null;
          const mergedHeatmapData = getMergedHeatmap(
            heatmapsInScenario,
            projectTestDisplayNumbers,
            floorplansInProjectData,
          );
          mergedHeatmapData.testtype = TEST_TYPES.VERIFICATION;
          mergedHeatmapData.heatmaps = [mergedHeatmapData];

          const canvasImg = drawImageWithHeatmapDataOnCanvas(
            floorplansDataObj[floorplanId].img,
            mergedHeatmapData,
            false,
          );

          imageObj = {
            data: canvasImg.url,
            size: {
              width: canvasImg.width,
              height: canvasImg.height,
            },
          };

          const margins = PRESENTATION_DEFAULT_SIZE.w * 0.05;

          const heatmapMiddlePercent =
            PRESENTATION_DEFAULT_LEFT_MARGIN / PRESENTATION_DEFAULT_SIZE.w +
            (1 -
              margins * 2 -
              0 / PRESENTATION_DEFAULT_SIZE.w -
              PRESENTATION_DEFAULT_LEFT_MARGIN / PRESENTATION_DEFAULT_SIZE.w) /
              2;

          const slideImgSizeInInch = getScaledImageSize(
            { width: imageObj.size.width, height: imageObj.size.height },
            {
              width:
                PRESENTATION_DEFAULT_SIZE.w -
                PRESENTATION_DEFAULT_LEFT_MARGIN -
                margins * 2 -
                0,
              height: PRESENTATION_DEFAULT_SIZE.h * 0.68,
            },
          );

          slide.addImage({
            data: imageObj.data,
            x: `${
              (heatmapMiddlePercent +
                margins -
                slideImgSizeInInch.width / 2 / PRESENTATION_DEFAULT_SIZE.w) *
              100
            }%`,
            y: '25%',
            w: slideImgSizeInInch.width,
            h: slideImgSizeInInch.height,
          });
        }
      }

      let fileName = `PID_${projectId}_floorplan_level_heatmaps.pptx`;
      pres.writeFile({ fileName });
    } catch (error) {
      console.error('Error downloading heatmaps', error);
    }
    setDownloadingHeatmapsProjectId(null);
  };

  const getMergedHeatmap = (
    heatmapsInScenario,
    projectTestDisplayNumbers,
    floorplansInProjectData,
  ) => {
    const mergedHeatmap = {
      buildingData: {},
      op: [],
      sp: [],
    };

    for (let heatmapData of heatmapsInScenario) {
      const heatmap = heatmapData.heatmaps?.[0];
      if (!heatmap) continue;

      const testResultPercent = getTestResultDisplayDataFromCalculatedResult(
        heatmapData.calculatedresult,
      );

      const { floorplan, sp } = heatmap;

      const spsForMerged = [];
      const shiftData = floorplansInProjectData[floorplan]?.cropData;

      const shiftX = shiftData?.x || 0;
      const shiftY = shiftData?.y || 0;
      const testNumber = projectTestDisplayNumbers[heatmapData.testid];
      for (let _sp of sp) {
        if (_sp.spnumber !== 'SP-001') continue;

        const newSp = Object.assign({}, _sp, {
          spcoordx: parseFloat(_sp.spcoordx) + shiftX,
          spcoordy: parseFloat(_sp.spcoordy) + shiftY,
          testNumber: testNumber ? `Test Number: ${testNumber}` : '',
          result: {
            displayValue: testResultPercent.displayValue,
            isValid: testResultPercent.isValid,
            pass: testResultPercent.pass,
          },
        });

        spsForMerged.push(newSp);
      }
      mergedHeatmap.sp.push(...spsForMerged);
    }

    return mergedHeatmap;
  };

  const getTestResultDisplayDataFromCalculatedResult = (calculatedresult) => {
    const testResObj = calculatedresult && JSON.parse(calculatedresult);

    /** Same as in results/getVerificationProjectSummary (Project Downloads > Results > Summary) */
    let minimumPercentSP;
    let _pass;
    let _displayValue;

    if (testResObj.testValid && testResObj.spDetails) {
      const regex = /([><%])/g;
      minimumPercentSP = Object.values(testResObj.spDetails).reduce(
        (min, curr) => {
          const { displayValue, pass } = curr;

          const spPercent =
            typeof displayValue === 'string' && displayValue.replace(regex, '');
          const floatPercent = parseFloat(spPercent);

          if (!floatPercent) return min;

          if (!min || floatPercent < min) {
            min = floatPercent;
            _displayValue = displayValue;
            _pass = pass;
          }

          return min;
        },
        '',
      );
    }

    return {
      displayValue: _displayValue ? _displayValue : 'N/A',
      value: typeof minimumPercentSP === 'number' ? minimumPercentSP : 'N/A',
      isValid: testResObj.testValid,
      pass: _pass,
    };
  };

  return (
    <Stack direction="column" spacing={2}>
      {projects
        .sort((a, b) => a.orderNumber - b.orderNumber)
        .map((project) => {
          return (
            <Stack key={project.id} direction="column" spacing={2}>
              <Stack direction="row">
                <Typography color="primary" variant="h5">
                  {project.name}
                </Typography>
                {!downloadingHeatmapsProjectId ? (
                  <Tooltip
                    title={'Download all heatmaps'}
                    arrow
                    placement="top"
                  >
                    <span>
                      <IconButton
                        color="primary"
                        sx={{ ml: 1, position: 'relative', top: '-3px' }}
                        onClick={() =>
                          handleProjectHeatmapsDownload(project.id)
                        }
                      >
                        <DownloadIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                  <IconButton
                    color="primary"
                    sx={{ ml: 1, position: 'relative', top: '-3px' }}
                    disabled
                    className={
                      downloadingHeatmapsProjectId === project.id
                        ? 'icon-btn-loading-color'
                        : ``
                    }
                  >
                    <DownloadIcon />
                  </IconButton>
                )}
              </Stack>
              <Stack sx={{ paddingLeft: 2 }}>
                <HeatmapsListByFloor
                  projectId={project.id}
                  heatmaps={project.heatmaps}
                />
              </Stack>
            </Stack>
          );
        })}
    </Stack>
  );
}

function ResultsBuildingProjectsDisplaySkeleton() {
  return (
    <Stack direction="column" spacing={2}>
      {Array(3)
        .fill(1)
        .map((_, i) => (
          <Stack
            key={`project-results-skeleton-${_ + i}`}
            direction="column"
            spacing={2}
          >
            <Typography color="primary" variant="h5">
              <Skeleton width={900} />
            </Typography>
            <Stack sx={{ paddingLeft: 2 }}>
              <Stack spacing={2}>
                {[1].map((floorNumber) => {
                  return (
                    <Stack
                      key={`project-results-skeleton-floor-${
                        _ + i
                      }-${floorNumber}`}
                    >
                      <Typography variant="h6">
                        <Skeleton width={200} />
                      </Typography>
                      <Stack sx={{ mt: 1, ml: 2 }} spacing={1}>
                        {Array(3)
                          .fill(1)
                          .map((_, i) => {
                            return (
                              <Stack
                                key={`project-results-skeleton-fp-name-${
                                  _ + i
                                }`}
                                direction="row"
                                spacing={1}
                              >
                                <ImageIcon color="disabled" />
                                <Typography gutterBottom variant="body1">
                                  <Skeleton width={440} />
                                </Typography>
                              </Stack>
                            );
                          })}
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
            </Stack>
          </Stack>
        ))}
    </Stack>
  );
}
